import React, { useState, useEffect, Fragment } from 'react';
import {
  ArrowUpIcon,
  ArrowDownIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ChevronUpDownIcon,
  ArrowRightCircleIcon,
} from '@heroicons/react/24/outline';
import { Dialog, Transition, Listbox } from '@headlessui/react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useData } from '../../context/DataContext';
import Breadcrumbs from '../common/Breadcrumbs';
import {
  calculatePortfolioGain,
  calculateReturnPercentage,
} from '../../utils/calculations';
import {
  formatCurrency,
  formatCurrencyWithSign,
  formatPercentage,
  formatPercentageWithSign,
  formatDate,
  formatTransactionType,
  getValueColor,
} from '../../utils/formatters';
import TransactionModal from '../Transactions/TransactionModal';
import MoveTransactionModal from '../Transactions/MoveTransactionModal';
import AllocationModal from './AllocationModal';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip, Legend, ComposedChart, Line, XAxis, YAxis, Area } from 'recharts';
import { FINANCIAL_COLORS } from '../../utils/colors';
import { convertCurrency } from '../../utils/calculations';
import dayjs from 'dayjs';

const Portfolio = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { portfolios, snapshots, transactions, goals, updateGoal, deleteTransaction } = useData();
  const [isTransactionModalVisible, setIsTransactionModalVisible] = useState(false);
  const [isMoveTransactionModalVisible, setIsMoveTransactionModalVisible] = useState(false);
  const [isAllocationModalVisible, setIsAllocationModalVisible] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [movingTransaction, setMovingTransaction] = useState(null);
  const [editingAllocation, setEditingAllocation] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // { type: 'allocation' | 'transaction', id: ... }
  const [projectionYears, setProjectionYears] = useState(5); // Time horizon for projections
  const [chartAggregation, setChartAggregation] = useState('monthly'); // Chart data aggregation
  const [transactionSortConfig, setTransactionSortConfig] = useState({ key: 'date', direction: 'desc' });
  const [snapshotSortConfig, setSnapshotSortConfig] = useState({ key: 'date', direction: 'desc' });

  const portfolio = portfolios.find(p => p.id === id);

  // FIX: Limit snapshots to last 3 years to prevent performance issues
  // Loading 5+ years (1,825+ daily snapshots) causes rendering slowdowns
  const threeYearsAgo = dayjs().subtract(3, 'years').format('YYYY-MM-DD');
  const portfolioSnapshots = snapshots
    .filter(s => s.portfolioId === id && s.date >= threeYearsAgo);

  const portfolioTransactions = transactions.filter(t => t.portfolioId === id);

  // Portfolio totals are now automatically calculated by useEffect in DataContext

  // FIX: Standardize return rate defaults across the component
  // Helper function to get consistent return rates with proper fallbacks
  const getReturnRates = (portfolio) => {
    if (!portfolio) return { low: 3, average: 5, high: 8 };

    const low = portfolio.lowReturn ?? 3;
    const average = portfolio.averageReturn ?? portfolio.estimatedReturnRate ?? 5;
    // High return: use highReturn if set, otherwise optimisticReturn, otherwise average+3, otherwise 8
    const high = portfolio.highReturn ?? portfolio.optimisticReturn ??
                 ((portfolio.averageReturn || portfolio.estimatedReturnRate) ?
                   (portfolio.averageReturn || portfolio.estimatedReturnRate) + 3 : 8);

    return { low, average, high };
  };

  const returnRates = getReturnRates(portfolio);

  // Get allocations for this portfolio
  const portfolioAllocations = goals
    .map(goal => {
      const allocation = goal.allocations?.find(a => a.portfolioId === id);
      if (!allocation) return null;
      return {
        ...allocation,
        goalId: goal.id,
        goalName: goal.name,
        goalEmoji: goal.emoji,
        goalColor: goal.color,
        goalCurrency: goal.currency,
      };
    })
    .filter(Boolean);

  if (!portfolio) {
    return (
      <div className="p-6">
        <p className="text-gray-600">Portfolio not found</p>
      </div>
    );
  }

  const totalGain = calculatePortfolioGain(portfolio.currentValue, portfolio.costBasis);
  const returnPercentage = calculateReturnPercentage(portfolio.currentValue, portfolio.costBasis);

  // Combine historical and projection data
  const getCombinedChartData = () => {
    const today = dayjs();
    const currentValue = portfolio.currentValue;
    const currentCostBasis = portfolio.costBasis;
    // FIX: Use standardized return rates
    const { low: lowReturn, average: averageReturn, high: optimisticReturn } = returnRates;

    const monthsToProject = projectionYears * 12;
    const chartData = [];

    // Add historical snapshots
    const snapshotDates = portfolioSnapshots.map(s => s.date);
    const allHistoricalDates = [...new Set([...snapshotDates, today.format('YYYY-MM-DD')])].sort();

    allHistoricalDates.forEach(date => {
      let value = 0;
      let costBasis = 0;

      if (date === today.format('YYYY-MM-DD')) {
        value = currentValue;
        costBasis = currentCostBasis;
      } else {
        const snapshotsUpToDate = portfolioSnapshots
          .filter(s => s.date <= date)
          .sort((a, b) => new Date(b.date) - new Date(a.date));

        const snapshot = snapshotsUpToDate[0];
        value = snapshot ? snapshot.value : 0;
        costBasis = snapshot ? snapshot.costBasis : 0;
      }

      chartData.push({
        date,
        displayDate: dayjs(date).format('MMM D, YYYY'),
        value,
        costBasis,
        gain: value - costBasis,
        lowProjection: date === today.format('YYYY-MM-DD') ? currentValue : null,
        averageProjection: date === today.format('YYYY-MM-DD') ? currentValue : null,
        optimisticProjection: date === today.format('YYYY-MM-DD') ? currentValue : null,
        isProjection: false,
      });
    });

    // Add projection data with contributions
    const monthlyLowRate = lowReturn / 100 / 12;
    const monthlyAverageRate = averageReturn / 100 / 12;
    const monthlyOptimisticRate = optimisticReturn / 100 / 12;
    const monthlyContribution = portfolio.monthlyContribution || 0;

    // Initialize projection values
    let lowValue = currentValue;
    let averageValue = currentValue;
    let optimisticValue = currentValue;

    for (let month = 1; month <= monthsToProject; month++) {
      const date = today.add(month, 'month');

      // Apply compound growth and add monthly contribution
      lowValue = lowValue * (1 + monthlyLowRate) + monthlyContribution;
      averageValue = averageValue * (1 + monthlyAverageRate) + monthlyContribution;
      optimisticValue = optimisticValue * (1 + monthlyOptimisticRate) + monthlyContribution;

      chartData.push({
        date: date.format('YYYY-MM-DD'),
        displayDate: date.format('MMM YYYY'),
        value: null,
        costBasis: null,
        gain: null,
        lowProjection: lowValue,
        averageProjection: averageValue,
        optimisticProjection: optimisticValue,
        isProjection: true,
      });
    }

    return chartData;
  };

  // Aggregate chart data based on selected breakdown
  const aggregateChartData = (data, aggregation) => {
    if (aggregation === 'monthly') {
      return data; // No aggregation needed for monthly
    }

    // Yearly aggregation - keep only December data points (or last month of year)
    const aggregated = [];
    const yearlyData = {};

    data.forEach(point => {
      const date = dayjs(point.date);
      const year = date.year();

      // Always keep the latest data point for each year
      if (!yearlyData[year]) {
        yearlyData[year] = point;
      } else {
        yearlyData[year] = point;
      }
    });

    // Convert to array and update display dates for yearly view
    Object.keys(yearlyData).sort().forEach(year => {
      const point = { ...yearlyData[year] };
      point.displayDate = dayjs(point.date).format('YYYY');
      aggregated.push(point);
    });

    return aggregated;
  };

  const rawChartData = getCombinedChartData();
  const combinedChartData = aggregateChartData(rawChartData, chartAggregation);

  // Transaction handlers
  const handleAddTransaction = () => {
    setEditingTransaction(null);
    setIsTransactionModalVisible(true);
  };

  const handleEditTransaction = (transaction) => {
    setEditingTransaction(transaction);
    setIsTransactionModalVisible(true);
  };

  const handleDeleteTransaction = (transactionId) => {
    deleteTransaction(transactionId);
    setDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  const handleCloseTransactionModal = () => {
    setEditingTransaction(null);
    setIsTransactionModalVisible(false);
  };

  // Allocation handlers
  const handleAddAllocation = () => {
    setEditingAllocation(null);
    setIsAllocationModalVisible(true);
  };

  const handleEditAllocation = (allocation) => {
    setEditingAllocation(allocation);
    setIsAllocationModalVisible(true);
  };

  const handleDeleteAllocation = (goalId) => {
    const goal = goals.find(g => g.id === goalId);
    if (!goal) return;

    const updatedAllocations = (goal.allocations || []).filter(a => a.portfolioId !== id);
    updateGoal(goal.id, { allocations: updatedAllocations });
    setDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  const handleCloseAllocationModal = () => {
    setEditingAllocation(null);
    setIsAllocationModalVisible(false);
  };

  // Calculate allocated value with null safety
  const calculateAllocatedValue = (allocation) => {
    const value = allocation?.value ?? 0;
    if (allocation?.type === 'percentage') {
      return (portfolio?.currentValue ?? 0) * (value / 100);
    } else {
      return value;
    }
  };

  // Calculate total allocated with NaN protection
  const totalAllocated = portfolioAllocations.reduce((sum, alloc) => {
    const value = calculateAllocatedValue(alloc);
    return sum + (isNaN(value) ? 0 : value);
  }, 0);

  // Prepare chart data
  const allocationChartData = portfolioAllocations.map(alloc => {
    const value = calculateAllocatedValue(alloc);
    return {
      name: alloc.goalName,
      value: isNaN(value) ? 0 : value,
      emoji: alloc.goalEmoji,
      color: alloc.goalColor,
    };
  });

  const unallocated = (portfolio?.currentValue ?? 0) - totalAllocated;
  const isOverAllocated = unallocated < -0.01;
  if (unallocated > 0.01) {
    allocationChartData.push({
      name: 'Unallocated',
      value: unallocated,
      emoji: '💰',
      color: '#d9d9d9',
    });
  }

  // Render Allocation Table
  const renderAllocationTable = () => {
    if (portfolioAllocations.length === 0) {
      return (
        <div className="p-6 text-center">
          <p className="text-xs text-gray-600">
            {goals.length === 0
              ? 'Create goals first to start allocating this account'
              : 'No allocations yet. Allocate this account to your goals.'}
          </p>
        </div>
      );
    }

    return (
      <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
        <table className="w-full border-collapse text-xs">
          <thead className="sticky top-0 z-10 bg-white">
            <tr className="border-b-2 border-gray-300">
              <th className="p-2 text-left font-semibold bg-white">Goal</th>
              <th className="p-2 text-left font-semibold w-24 bg-white">Type</th>
              <th className="p-2 text-right font-semibold w-28 bg-white">Value</th>
              <th className="p-2 text-right font-semibold w-28 bg-white">Allocated</th>
              <th className="p-2 text-center font-semibold w-20 bg-white">Actions</th>
            </tr>
          </thead>
          <tbody>
            {portfolioAllocations.map((record) => (
              <tr key={record.goalId} className="border-b border-gray-200">
                <td className="p-2">
                  <div className="flex gap-2 items-center">
                    <span className="text-base">{record.goalEmoji}</span>
                    <span className="text-xs font-semibold">{record.goalName}</span>
                  </div>
                </td>
                <td className="p-2">
                  <span className="inline-block bg-blue-500 text-white px-2 py-0.5 rounded text-[10px]">
                    {record.type === 'percentage' || !record.type ? 'Percentage' : 'Amount'}
                  </span>
                </td>
                <td className="p-2 text-right">
                  <span className="text-xs font-semibold">
                    {record.type === 'percentage' || !record.type ? `${record.value ?? 0}%` : formatCurrency(record.value ?? 0, portfolio.currency, 2)}
                  </span>
                </td>
                <td className="p-2 text-right">
                  <span className="text-xs text-green-700 font-semibold">
                    {formatCurrency(calculateAllocatedValue(record), portfolio.currency, 0)}
                  </span>
                </td>
                <td className="p-2 text-center">
                  <div className="flex gap-1 justify-center">
                    <button
                      onClick={() => handleEditAllocation(record)}
                      className="p-1 hover:bg-gray-100 rounded transition-colors"
                      aria-label="Edit allocation"
                    >
                      <PencilIcon className="w-4 h-4 text-gray-600" />
                    </button>
                    <button
                      onClick={() => {
                        setDeleteTarget({ type: 'allocation', id: record.goalId });
                        setDeleteDialogOpen(true);
                      }}
                      className="p-1 hover:bg-gray-100 rounded transition-colors"
                      aria-label="Delete allocation"
                    >
                      <TrashIcon className="w-4 h-4 text-gray-600" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Render Transaction Table
  const handleTransactionSort = (key) => {
    let direction = 'asc';
    if (transactionSortConfig.key === key && transactionSortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setTransactionSortConfig({ key, direction });
  };

  const getTransactionSortIcon = (columnKey) => {
    if (transactionSortConfig.key !== columnKey) {
      return <ChevronUpDownIcon className="w-3 h-3 text-gray-400 opacity-50" />;
    }
    return transactionSortConfig.direction === 'asc'
      ? <ArrowUpIcon className="w-3 h-3 text-gray-700" />
      : <ArrowDownIcon className="w-3 h-3 text-gray-700" />;
  };

  const renderTransactionTable = () => {
    if (portfolioTransactions.length === 0) {
      return (
        <div className="p-6 text-center">
          <p className="text-xs text-gray-600">No transactions yet</p>
        </div>
      );
    }

    const sortedTransactions = [...portfolioTransactions].sort((a, b) => {
      let aValue, bValue;

      switch (transactionSortConfig.key) {
        case 'date':
          aValue = new Date(a.date);
          bValue = new Date(b.date);
          break;
        case 'type':
          aValue = a.type || '';
          bValue = b.type || '';
          break;
        case 'amount':
          aValue = a.amount || 0;
          bValue = b.amount || 0;
          break;
        case 'description':
          aValue = a.description?.toLowerCase() || '';
          bValue = b.description?.toLowerCase() || '';
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return transactionSortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return transactionSortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return (
      <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
        <table className="w-full border-collapse text-xs">
          <thead className="sticky top-0 z-10 bg-white">
            <tr className="border-b-2 border-gray-300">
              <th className="p-2 text-left font-semibold w-28 bg-white">
                <button
                  onClick={() => handleTransactionSort('date')}
                  className="flex items-center gap-1 hover:text-gray-900 transition-colors"
                >
                  Date
                  {getTransactionSortIcon('date')}
                </button>
              </th>
              <th className="p-2 text-left font-semibold w-28 bg-white">
                <button
                  onClick={() => handleTransactionSort('type')}
                  className="flex items-center gap-1 hover:text-gray-900 transition-colors"
                >
                  Type
                  {getTransactionSortIcon('type')}
                </button>
              </th>
              <th className="p-2 text-right font-semibold w-32 bg-white">
                <button
                  onClick={() => handleTransactionSort('amount')}
                  className="flex items-center gap-1 hover:text-gray-900 transition-colors ml-auto"
                >
                  Amount
                  {getTransactionSortIcon('amount')}
                </button>
              </th>
              <th className="p-2 text-left font-semibold w-24 bg-white">From</th>
              <th className="p-2 text-left font-semibold bg-white">
                <button
                  onClick={() => handleTransactionSort('description')}
                  className="flex items-center gap-1 hover:text-gray-900 transition-colors"
                >
                  Description
                  {getTransactionSortIcon('description')}
                </button>
              </th>
              <th className="p-2 text-center font-semibold w-20 bg-white">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedTransactions.map((record) => {
              const colors = {
                contribution: '#10B981',
                withdrawal: '#EF4444',
                dividend: '#3B82F6',
                fee: '#F59E0B',
                manual: '#8B5CF6',
              };
              let value;
              if (record.type === 'contribution' || record.type === 'dividend') {
                value = record.amount;
              } else if (record.type === 'manual') {
                value = record.amount;
              } else {
                value = -record.amount;
              }
              return (
                <tr key={record.id} className="border-b border-gray-200">
                  <td className="p-2">
                    <span className="text-xs">{formatDate(record.date)}</span>
                  </td>
                  <td className="p-2">
                    <span
                      className="inline-block text-white px-2 py-0.5 rounded text-[11px]"
                      style={{ backgroundColor: colors[record.type] }}
                    >
                      {formatTransactionType(record.type)}
                    </span>
                  </td>
                  <td className="p-2 text-right">
                    <span className="text-sm font-semibold" style={{ color: getValueColor(value) }}>
                      {formatCurrencyWithSign(value, portfolio.currency, 2)}
                    </span>
                  </td>
                  <td className="p-2">
                    <span className="text-xs text-gray-700">{record.from || '-'}</span>
                  </td>
                  <td className="p-2">
                    <span className="text-xs">{record.description}</span>
                  </td>
                  <td className="p-2 text-center">
                    <div className="flex gap-1 justify-center">
                      <button
                        onClick={() => handleEditTransaction(record)}
                        className="p-1 hover:bg-gray-100 rounded transition-colors"
                        aria-label="Edit transaction"
                        title="Edit"
                      >
                        <PencilIcon className="w-4 h-4 text-gray-600" />
                      </button>
                      <button
                        onClick={() => {
                          setMovingTransaction(record);
                          setIsMoveTransactionModalVisible(true);
                        }}
                        className="p-1 hover:bg-blue-100 rounded transition-colors"
                        aria-label="Move to another account"
                        title="Move to another account"
                      >
                        <ArrowRightCircleIcon className="w-4 h-4 text-blue-600" />
                      </button>
                      <button
                        onClick={() => {
                          setDeleteTarget({ type: 'transaction', id: record.id });
                          setDeleteDialogOpen(true);
                        }}
                        className="p-1 hover:bg-gray-100 rounded transition-colors"
                        aria-label="Delete transaction"
                        title="Delete"
                      >
                        <TrashIcon className="w-4 h-4 text-gray-600" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  // Render Snapshot Table
  const handleSnapshotSort = (key) => {
    let direction = 'asc';
    if (snapshotSortConfig.key === key && snapshotSortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSnapshotSortConfig({ key, direction });
  };

  const getSnapshotSortIcon = (columnKey) => {
    if (snapshotSortConfig.key !== columnKey) {
      return <ChevronUpDownIcon className="w-3 h-3 text-gray-400 opacity-50" />;
    }
    return snapshotSortConfig.direction === 'asc'
      ? <ArrowUpIcon className="w-3 h-3 text-gray-700" />
      : <ArrowDownIcon className="w-3 h-3 text-gray-700" />;
  };

  const renderSnapshotTable = () => {
    const sortedSnapshots = [...portfolioSnapshots].sort((a, b) => {
      let aValue, bValue;

      switch (snapshotSortConfig.key) {
        case 'date':
          aValue = new Date(a.date);
          bValue = new Date(b.date);
          break;
        case 'value':
          aValue = a.value || 0;
          bValue = b.value || 0;
          break;
        case 'costBasis':
          aValue = a.costBasis || 0;
          bValue = b.costBasis || 0;
          break;
        case 'gain':
          aValue = (a.value || 0) - (a.costBasis || 0);
          bValue = (b.value || 0) - (b.costBasis || 0);
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return snapshotSortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return snapshotSortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return (
      <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
        <table className="w-full border-collapse text-xs">
          <thead className="sticky top-0 z-10 bg-white">
            <tr className="border-b-2 border-gray-300">
              <th className="p-2 text-left font-semibold bg-white">
                <button
                  onClick={() => handleSnapshotSort('date')}
                  className="flex items-center gap-1 hover:text-gray-900 transition-colors"
                >
                  Date
                  {getSnapshotSortIcon('date')}
                </button>
              </th>
              <th className="p-2 text-right font-semibold bg-white">
                <button
                  onClick={() => handleSnapshotSort('value')}
                  className="flex items-center gap-1 hover:text-gray-900 transition-colors ml-auto"
                >
                  Value
                  {getSnapshotSortIcon('value')}
                </button>
              </th>
              <th className="p-2 text-right font-semibold bg-white">
                <button
                  onClick={() => handleSnapshotSort('costBasis')}
                  className="flex items-center gap-1 hover:text-gray-900 transition-colors ml-auto"
                >
                  Cost Basis
                  {getSnapshotSortIcon('costBasis')}
                </button>
              </th>
              <th className="p-2 text-right font-semibold bg-white">
                <button
                  onClick={() => handleSnapshotSort('gain')}
                  className="flex items-center gap-1 hover:text-gray-900 transition-colors ml-auto"
                >
                  Gain/Loss
                  {getSnapshotSortIcon('gain')}
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedSnapshots.map((record) => {
              const gain = record.value - record.costBasis;
              const gainPercentage = calculateReturnPercentage(record.value, record.costBasis);
              return (
                <tr key={record.id} className="border-b border-gray-200">
                  <td className="p-2">
                    <span className="text-xs">{formatDate(record.date)}</span>
                  </td>
                  <td className="p-2 text-right">
                    <span className="text-xs">{formatCurrency(record.value, portfolio.currency, 0)}</span>
                  </td>
                  <td className="p-2 text-right">
                    <span className="text-xs">{formatCurrency(record.costBasis, portfolio.currency, 0)}</span>
                  </td>
                  <td className="p-2 text-right">
                    <div className="flex flex-col items-end gap-0.5">
                      <span className="text-sm" style={{ color: getValueColor(gain) }}>
                        {formatCurrencyWithSign(gain, portfolio.currency, 0)}
                      </span>
                      <span className="text-[11px] text-gray-600">
                        {formatPercentageWithSign(gainPercentage)}
                      </span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div>
      {/* Breadcrumbs */}
      <Breadcrumbs
        items={[
          { label: 'Accounts', link: '/accounts' },
          { label: portfolio.name },
        ]}
      />

      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1">{portfolio.name}</h3>
          <div className="flex gap-1">
            <span className="inline-block bg-blue-500 text-white px-1 rounded text-[10px]">
              {portfolio.type}
            </span>
            <span className="inline-block bg-green-500 text-white px-1 rounded text-[10px]">
              {portfolio.currency}
            </span>
          </div>
        </div>
        <button
          onClick={() => navigate(`/accounts/edit/${portfolio.id}`)}
          className="flex items-center gap-2 px-4 py-2 text-white rounded-lg font-medium transition-colors"
          style={{ backgroundColor: '#012E58' }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#01234a'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#012E58'}
        >
          <PencilIcon className="w-5 h-5" />
          <span>Edit Account</span>
        </button>
      </div>

      {/* Account Overview - Single Line */}
      <div className="bg-white p-3 rounded-lg border border-gray-200 mb-4">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-2">
          {/* Current Value */}
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-600">Current Value</span>
            <span className="text-base font-bold text-gray-900">
              {formatCurrency(portfolio.currentValue, portfolio.currency, 0)}
            </span>
          </div>

          {/* Separator */}
          <div className="w-px h-5 bg-gray-300"></div>

          {/* Cost Basis */}
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-600">Cost Basis</span>
            <span className="text-base font-bold text-gray-900">
              {formatCurrency(portfolio.costBasis, portfolio.currency, 0)}
            </span>
          </div>

          {/* Separator */}
          <div className="w-px h-5 bg-gray-300"></div>

          {/* Total Return */}
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-600">Total Return</span>
            <div className="flex items-center gap-1">
              {totalGain >= 0 ? (
                <ArrowUpIcon className="w-3 h-3" style={{ color: getValueColor(totalGain) }} />
              ) : (
                <ArrowDownIcon className="w-3 h-3" style={{ color: getValueColor(totalGain) }} />
              )}
              <span className="text-base font-bold" style={{ color: getValueColor(totalGain) }}>
                {formatCurrencyWithSign(totalGain, portfolio.currency, 0)}
              </span>
              <span className="text-sm font-semibold" style={{ color: getValueColor(totalGain) }}>
                {formatPercentageWithSign(returnPercentage)}
              </span>
            </div>
          </div>
        </div>

        {/* Account Value Changes Row */}
        <div className="pt-2 border-t border-gray-200">
          <div className="flex gap-3 items-center flex-wrap">
            <span className="text-[10px] text-gray-600 font-semibold uppercase">Value Change:</span>
            {(() => {
              const today = dayjs();

              // Helper to get account value at a specific date
              const getValueAtDate = (targetDate) => {
                const accountSnapshots = portfolioSnapshots
                  .filter(s => s.date <= targetDate)
                  .sort((a, b) => new Date(b.date) - new Date(a.date));
                return accountSnapshots.length > 0 ? accountSnapshots[0].value : null;
              };

              // Find last update date
              const lastUpdateDate = portfolioSnapshots.length > 0
                ? portfolioSnapshots.sort((a, b) => new Date(b.date) - new Date(a.date))[0].date
                : null;

              // Find earliest date
              const earliestDate = portfolioSnapshots.length > 0
                ? portfolioSnapshots.sort((a, b) => new Date(a.date) - new Date(b.date))[0].date
                : null;

              const periods = [
                { label: 'Last Update', date: lastUpdateDate },
                { label: 'Week', date: today.subtract(7, 'day').format('YYYY-MM-DD') },
                { label: 'Month', date: today.subtract(1, 'month').format('YYYY-MM-DD') },
                { label: '6 Months', date: today.subtract(6, 'month').format('YYYY-MM-DD') },
                { label: 'Year', date: today.subtract(1, 'year').format('YYYY-MM-DD') },
                { label: 'All Time', date: earliestDate },
              ];

              return (
                <>
                  {periods.map((period, index) => {
                    if (!period.date) {
                      return (
                        <div key={index} className="flex items-center gap-1">
                          <span className="text-[10px] text-gray-500">{period.label}</span>
                          <span className="text-xs text-gray-400">n/a</span>
                        </div>
                      );
                    }

                    const historicalValue = getValueAtDate(period.date);
                    if (historicalValue === null || historicalValue === portfolio.currentValue) {
                      return (
                        <div key={index} className="flex items-center gap-1">
                          <span className="text-[10px] text-gray-500">{period.label}</span>
                          <span className="text-xs text-gray-400">n/a</span>
                        </div>
                      );
                    }

                    const change = portfolio.currentValue - historicalValue;
                    const percentChange = historicalValue > 0 ? (change / historicalValue) * 100 : 0;

                    return (
                      <div key={index} className="flex items-center gap-1">
                        <span className="text-[10px] text-gray-500">{period.label}</span>
                        <span className={`text-xs font-semibold ${change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {change >= 0 ? '+' : '-'}{formatCurrency(Math.abs(change), portfolio.currency, 0)} ({formatPercentageWithSign(percentChange, 0)})
                        </span>
                      </div>
                    );
                  })}
                </>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Combined Historical & Projected Value Chart */}
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-4">
        <div className="flex justify-between items-center mb-3">
          <h4 className="text-sm font-semibold">Account Value Over Time & Projections</h4>
          <div className="flex items-center gap-4">
            {/* Period */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-600">Period:</span>
              <Listbox value={projectionYears} onChange={setProjectionYears}>
                <div className="relative">
                  <Listbox.Button className="relative cursor-pointer rounded-lg bg-white py-1.5 pl-3 pr-8 text-left border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs min-w-[80px]">
                    <span className="block truncate">{projectionYears} {projectionYears === 1 ? 'Year' : 'Years'}</span>
                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                      <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                    </span>
                  </Listbox.Button>
                  <Transition
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-32 overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none text-xs right-0">
                      {[1, 3, 5, 10, 15, 20, 30].map((years) => (
                        <Listbox.Option
                          key={years}
                          value={years}
                          className={({ active }) =>
                            `relative cursor-pointer select-none py-2 pl-8 pr-4 ${
                              active ? 'bg-blue-50 text-blue-900' : 'text-gray-900'
                            }`
                          }
                        >
                          {({ selected }) => (
                            <>
                              <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                {years} {years === 1 ? 'Year' : 'Years'}
                              </span>
                              {selected && (
                                <span className="absolute inset-y-0 left-0 flex items-center pl-2 text-blue-600">
                                  <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                </span>
                              )}
                            </>
                          )}
                        </Listbox.Option>
                      ))}
                    </Listbox.Options>
                  </Transition>
                </div>
              </Listbox>
            </div>

            {/* Breakdown */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-600">Breakdown:</span>
              <Listbox value={chartAggregation} onChange={setChartAggregation}>
                <div className="relative">
                  <Listbox.Button className="relative cursor-pointer rounded-lg bg-white py-1.5 pl-3 pr-8 text-left border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs min-w-[80px]">
                    <span className="block truncate capitalize">{chartAggregation}</span>
                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                      <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                    </span>
                  </Listbox.Button>
                  <Transition
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-32 overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none text-xs right-0">
                      {['monthly', 'yearly'].map((agg) => (
                        <Listbox.Option
                          key={agg}
                          value={agg}
                          className={({ active }) =>
                            `relative cursor-pointer select-none py-2 pl-8 pr-4 ${
                              active ? 'bg-blue-50 text-blue-900' : 'text-gray-900'
                            }`
                          }
                        >
                          {({ selected }) => (
                            <>
                              <span className={`block truncate capitalize ${selected ? 'font-semibold' : 'font-normal'}`}>
                                {agg}
                              </span>
                              {selected && (
                                <span className="absolute inset-y-0 left-0 flex items-center pl-2 text-blue-600">
                                  <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                </span>
                              )}
                            </>
                          )}
                        </Listbox.Option>
                      ))}
                    </Listbox.Options>
                  </Transition>
                </div>
              </Listbox>
            </div>
          </div>
        </div>
        <div className="flex gap-4 text-xs mb-3 justify-end">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#2563eb' }}></div>
            <span className="text-gray-600">Current Value</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#6b7280' }}></div>
            <span className="text-gray-600">Cost Basis</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#dc2626' }}></div>
            <span className="text-gray-600">Low ({formatPercentage(returnRates.low, 1)})</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#7c3aed' }}></div>
            <span className="text-gray-600">Average ({formatPercentage(returnRates.average, 1)})</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#059669' }}></div>
            <span className="text-gray-600">High ({formatPercentage(returnRates.high, 1)})</span>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={350}>
          <ComposedChart data={combinedChartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="valueGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.5}/>
                <stop offset="50%" stopColor="#60a5fa" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#93c5fd" stopOpacity={0.1}/>
              </linearGradient>
              <linearGradient id="costBasisGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#6b7280" stopOpacity={0.5}/>
                <stop offset="50%" stopColor="#9ca3af" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#d1d5db" stopOpacity={0.1}/>
              </linearGradient>
              <linearGradient id="lowGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#ef4444" stopOpacity={0.5}/>
                <stop offset="50%" stopColor="#f87171" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#fca5a5" stopOpacity={0.1}/>
              </linearGradient>
              <linearGradient id="averageGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.5}/>
                <stop offset="50%" stopColor="#a78bfa" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#c4b5fd" stopOpacity={0.1}/>
              </linearGradient>
              <linearGradient id="optimisticGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#10b981" stopOpacity={0.5}/>
                <stop offset="50%" stopColor="#34d399" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#6ee7b7" stopOpacity={0.1}/>
              </linearGradient>
            </defs>
            <XAxis
              dataKey="displayDate"
              stroke="#6b7280"
              style={{ fontSize: 11 }}
              interval="preserveStartEnd"
            />
            <YAxis
              stroke="#6b7280"
              style={{ fontSize: 11 }}
              tickFormatter={(value) => formatCurrency(value, portfolio.currency, 0)}
            />
            <RechartsTooltip
              allowEscapeViewBox={{ x: false, y: true }}
              wrapperStyle={{ zIndex: 1000, pointerEvents: 'none' }}
              position={{ y: 0 }}
              content={({ active, payload, label, coordinate, viewBox }) => {
                if (!active || !payload) return null;

                // Find current and previous data points
                const currentIndex = combinedChartData.findIndex(d => d.displayDate === label);
                const previousData = currentIndex > 0 ? combinedChartData[currentIndex - 1] : null;

                // Filter to show only unique dataKeys (avoid duplicates from Area + Line)
                const uniqueData = {};
                payload.forEach(entry => {
                  if (entry.value != null && !uniqueData[entry.dataKey]) {
                    uniqueData[entry.dataKey] = entry;
                  }
                });

                const labels = {
                  value: 'Current Value',
                  costBasis: 'Cost Basis',
                  lowProjection: 'Low',
                  averageProjection: 'Average',
                  optimisticProjection: 'High',
                };

                // Calculate if we're near the right edge (within 200px of the right side)
                const isNearRightEdge = coordinate && viewBox && (viewBox.x + viewBox.width - coordinate.x < 200);

                return (
                  <div style={{
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: 8,
                    fontSize: '13px',
                    padding: '8px 12px',
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
                    transform: isNearRightEdge ? 'translateX(-100%)' : 'translateX(10px)',
                    marginLeft: isNearRightEdge ? '-10px' : '0px',
                  }}>
                    <p style={{ marginBottom: '4px', fontWeight: 600 }}>{label}</p>
                    {Object.values(uniqueData).map((entry, index) => {
                      const currentValue = entry.value;
                      const previousValue = previousData?.[entry.dataKey];
                      const change = previousValue != null ? currentValue - previousValue : null;
                      const percentChange = previousValue != null && previousValue !== 0
                        ? ((currentValue - previousValue) / previousValue) * 100
                        : null;

                      return (
                        <div key={index} style={{ margin: '4px 0' }}>
                          <p style={{ margin: 0, color: entry.color }}>
                            <span style={{ fontWeight: 500 }}>{labels[entry.dataKey] || entry.dataKey}:</span>{' '}
                            {formatCurrency(currentValue, portfolio.currency, 0)}
                          </p>
                          {change != null && percentChange != null && (
                            <p style={{
                              margin: 0,
                              marginLeft: '8px',
                              fontSize: '11px',
                              color: change >= 0 ? '#10b981' : '#ef4444',
                              fontWeight: 500
                            }}>
                              {change >= 0 ? '↑' : '↓'} {formatCurrency(Math.abs(change), portfolio.currency, 0)} ({formatPercentageWithSign(percentChange, 1)})
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              }}
            />
            {/* Historical areas */}
            <Area
              type="monotone"
              dataKey="costBasis"
              stroke="none"
              fill="url(#costBasisGradient)"
              fillOpacity={1}
              isAnimationActive={false}
            />
            <Area
              type="monotone"
              dataKey="value"
              stroke="none"
              fill="url(#valueGradient)"
              fillOpacity={1}
              isAnimationActive={false}
            />
            {/* Projection areas */}
            <Area
              type="monotone"
              dataKey="lowProjection"
              stroke="none"
              fill="url(#lowGradient)"
              fillOpacity={1}
              isAnimationActive={false}
            />
            <Area
              type="monotone"
              dataKey="averageProjection"
              stroke="none"
              fill="url(#averageGradient)"
              fillOpacity={1}
              isAnimationActive={false}
            />
            <Area
              type="monotone"
              dataKey="optimisticProjection"
              stroke="none"
              fill="url(#optimisticGradient)"
              fillOpacity={1}
              isAnimationActive={false}
            />
            {/* Historical lines */}
            <Line
              type="monotone"
              dataKey="costBasis"
              stroke="#6b7280"
              strokeWidth={2}
              dot={{ r: 4, fill: '#6b7280', strokeWidth: 2, stroke: '#fff' }}
              activeDot={{ r: 6 }}
              name="costBasis"
              connectNulls={false}
              isAnimationActive={false}
            />
            <Line
              type="monotone"
              dataKey="value"
              stroke="#2563eb"
              strokeWidth={3}
              dot={{ r: 5, fill: '#2563eb', strokeWidth: 2, stroke: '#fff' }}
              activeDot={{ r: 7 }}
              name="value"
              connectNulls={false}
              isAnimationActive={false}
            />
            {/* Projection lines */}
            <Line
              type="monotone"
              dataKey="lowProjection"
              stroke="#dc2626"
              strokeWidth={3}
              strokeDasharray="5 5"
              dot={false}
              name="lowProjection"
              connectNulls={true}
              isAnimationActive={false}
            />
            <Line
              type="monotone"
              dataKey="averageProjection"
              stroke="#7c3aed"
              strokeWidth={3}
              strokeDasharray="5 5"
              dot={false}
              name="averageProjection"
              connectNulls={true}
              isAnimationActive={false}
            />
            <Line
              type="monotone"
              dataKey="optimisticProjection"
              stroke="#059669"
              strokeWidth={3}
              strokeDasharray="5 5"
              dot={false}
              name="optimisticProjection"
              connectNulls={true}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Fund Allocations */}
      <div className="bg-gray-50 rounded-lg border border-gray-200 mb-4">
        <div className="flex justify-between items-center p-3 border-b border-gray-200">
          <h4 className="text-sm font-semibold">Fund Allocations</h4>
          <button
            onClick={handleAddAllocation}
            disabled={goals.length === 0}
            className="flex items-center gap-1 px-3 py-1.5 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: '#012E58' }}
            onMouseEnter={(e) => !e.currentTarget.disabled && (e.currentTarget.style.backgroundColor = '#01234a')}
            onMouseLeave={(e) => !e.currentTarget.disabled && (e.currentTarget.style.backgroundColor = '#012E58')}
          >
            <PlusIcon className="w-4 h-4" />
            <span>Allocate to Goal</span>
          </button>
        </div>
        <div className={`grid ${allocationChartData.length > 0 ? 'grid-cols-1 lg:grid-cols-[1fr_400px]' : 'grid-cols-1'} gap-3 p-3`}>
          <div>
            {renderAllocationTable()}
          </div>
          {allocationChartData.length > 0 && (
            <div>
              <div className="text-center mb-2">
                <p className="text-xs font-semibold">Allocation Breakdown</p>
              </div>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={allocationChartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${formatPercentage(percent * 100, 0)}`}
                    outerRadius={70}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {allocationChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <RechartsTooltip
                    allowEscapeViewBox={{ x: true, y: true }}
                    wrapperStyle={{ zIndex: 1000 }}
                    formatter={(value) => formatCurrency(value, portfolio.currency, 0)}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="text-center mt-2">
                <div className="flex flex-col gap-1 items-center">
                  <p className="text-[11px] text-gray-600">
                    <strong>Total Allocated:</strong> {formatCurrency(totalAllocated, portfolio.currency, 0)}
                  </p>
                  {isOverAllocated ? (
                    <p className="text-[11px] text-red-600 font-semibold">
                      Shortfall: {formatCurrency(unallocated, portfolio.currency, 0)}
                    </p>
                  ) : (
                    <p className="text-[11px] text-gray-600">
                      <strong>Unallocated:</strong> {formatCurrency(unallocated, portfolio.currency, 0)}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Transactions */}
      <div className="bg-gray-50 rounded-lg border border-gray-200 mb-4">
        <div className="flex justify-between items-center p-3 border-b border-gray-200">
          <h4 className="text-sm font-semibold">Transactions</h4>
          <button
            onClick={handleAddTransaction}
            className="flex items-center gap-1 px-3 py-1.5 text-white rounded-lg text-sm font-medium transition-colors"
            style={{ backgroundColor: '#012E58' }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#01234a'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#012E58'}
          >
            <PlusIcon className="w-4 h-4" />
            <span>Add Transaction</span>
          </button>
        </div>
        {renderTransactionTable()}
      </div>

      {/* Historical Snapshots */}
      <div className="bg-gray-50 rounded-lg border border-gray-200">
        <div className="p-3 border-b border-gray-200">
          <h4 className="text-sm font-semibold">Historical Values</h4>
        </div>
        {renderSnapshotTable()}
      </div>

      {/* Delete Confirmation Dialog */}
      <Transition appear show={deleteDialogOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setDeleteDialogOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex-shrink-0">
                      <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                    </div>
                    <Dialog.Title as="h3" className="text-lg font-semibold text-gray-900">
                      Delete {deleteTarget?.type === 'allocation' ? 'Allocation' : 'Transaction'}
                    </Dialog.Title>
                  </div>

                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      {deleteTarget?.type === 'allocation'
                        ? 'Delete this allocation?'
                        : 'Delete this transaction?'}
                    </p>
                  </div>

                  <div className="mt-6 flex gap-3 justify-end">
                    <button
                      type="button"
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                      onClick={() => {
                        setDeleteDialogOpen(false);
                        setDeleteTarget(null);
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
                      onClick={() => {
                        if (deleteTarget?.type === 'allocation') {
                          handleDeleteAllocation(deleteTarget.id);
                        } else if (deleteTarget?.type === 'transaction') {
                          handleDeleteTransaction(deleteTarget.id);
                        }
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Add/Edit Transaction Modal */}
      <TransactionModal
        visible={isTransactionModalVisible}
        onCancel={handleCloseTransactionModal}
        transaction={editingTransaction}
        defaultPortfolioId={portfolio.id}
      />

      {/* Move Transaction Modal */}
      <MoveTransactionModal
        visible={isMoveTransactionModalVisible}
        onCancel={() => {
          setIsMoveTransactionModalVisible(false);
          setMovingTransaction(null);
        }}
        transaction={movingTransaction}
      />

      {/* Add/Edit Allocation Modal */}
      <AllocationModal
        visible={isAllocationModalVisible}
        onCancel={handleCloseAllocationModal}
        portfolioId={portfolio.id}
        allocation={editingAllocation}
      />
    </div>
  );
};

export default Portfolio;
